import React, { useEffect, useMemo, useState } from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { api } from "../services";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import { handlePlatformLogoError, resolveIntegrationLogo, usesWideIntegrationLogo } from "../utils/platformLogos";
import { getLocalizedPlatformName } from "../utils/platformNames";
import {
  LIVE_SUBSCRIPTION,
  appId,
  connectPath,
  connectedBranchCount,
  connectionsForApp,
  featureKeyForApp,
  formatDate,
  formatMinor,
  heldCoveringPlan,
  planForApp,
  sectionOf,
  subscriptionItemForApp,
} from "./subscription/subscriptionCatalog";
import riyalIcon from "./subscription/assets/riyal.svg";
import "./subscription/SubscriptionFlow.css";

const SECTIONS = [
  { id: "addon", title: "subscriptionFlow.loopsServices", pill: "subscriptionFlow.byLoops", pillClass: "new" },
  { id: "integration", title: "subscriptionFlow.delivery", pill: "subscriptionFlow.integrations", pillClass: "" },
  { id: "pos", title: "subscriptionFlow.pos", pill: "subscriptionFlow.posPill", pillClass: "teal" },
];

function AppManagement() {
  const { t, i18n } = useTranslation("translation");
  const { can } = usePermission();
  const canView = can(RBAC_MODULES.APP_MANAGEMENT, PERMISSION_ACTIONS.VIEW_APPS)
    || can(RBAC_MODULES.APP_MANAGEMENT, PERMISSION_ACTIONS.VIEW_INTEGRATIONS);
  const canAdd = can(RBAC_MODULES.APP_MANAGEMENT, PERMISSION_ACTIONS.ADD_INTEGRATIONS);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [connections, setConnections] = useState([]);
  const [renewsAt, setRenewsAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const [appsRes, bundleRes, connectionRes] = await Promise.all([
          api.get("/available-apps/categorized", { headers: { Authorization: `Bearer ${token}` } }),
          api.get("/subscriptions/bundle").catch(() => ({ data: { plans: [], subscription: null } })),
          api.get("/subscriptions/bundle/connections").catch(() => ({ data: { connections: [], renewsAt: null } })),
        ]);
        if (cancelled) return;
        const visible = (app) => app?.status !== "Pending" && !app?.isHidden;
        setApps([
          ...(appsRes.data?.categories?.addOns?.apps || []),
          ...(appsRes.data?.categories?.integrations?.apps || []),
        ].filter(visible));
        setPlans(bundleRes.data?.plans || []);
        setSubscription(bundleRes.data?.subscription || null);
        setConnections(connectionRes.data?.connections || []);
        setRenewsAt(connectionRes.data?.renewsAt || bundleRes.data?.subscription?.renewsAt || null);
      } catch (err) {
        if (!cancelled) toast.error(t("subscriptionFlow.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const coveringPlan = heldCoveringPlan(subscription, plans);
  const live = LIVE_SUBSCRIPTION.has(subscription?.status);

  const usageFor = (app) => {
    const key = featureKeyForApp(app);
    const used = connectedBranchCount(app, connections);
    const plan = planForApp(app, plans);
    const item = subscriptionItemForApp(app, subscription, plans);
    const entitled = Number(subscription?.entitlements?.[key]);
    const limit = coveringPlan && live
      ? Number(coveringPlan.branchLimit || 0)
      : (Number.isFinite(entitled) && entitled > 0 ? entitled : Number(item?.quantity || 0));
    const scheduled = (subscription?.scheduledChange?.items || []).find((row) => row.planCode === item?.planCode);
    const subscribed = Boolean(app.isSubscribed) || limit > 0 || Boolean(coveringPlan && live);
    const hasExisting = used > 0 || Boolean(app.isConnected);
    return { used, limit, item, plan, scheduled, subscribed, hasExisting, manageable: subscribed || hasExisting };
  };

  const openCard = (app) => {
    const disabled = app.status === "Inactive" || app.status === "Coming Soon";
    if (disabled) return;
    if (!canView && !canAdd) {
      toast.error(t("noPermissionToAccessThis"));
      return;
    }
    const usage = usageFor(app);
    if (!usage.manageable) {
      navigate(`/subscription?stage=${appId(app)}`);
      return;
    }
    navigate(connectPath(app));
  };

  const sections = useMemo(() => SECTIONS.map((section) => ({
    ...section,
    apps: apps.filter((app) => sectionOf(app) === section.id),
  })), [apps]);

  return (
    <div className="apps-management-page-root oh-page sf-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <header>
          <h1>{t("sidebar.appsManagement")}</h1>
          <p className="sf-lead">{t("appsManagementPage.lead")}</p>
        </header>
        {loading ? (
          <div className="sf-tiles">
            {[...Array(4)].map((_, index) => <Skeleton key={index} height={220} />)}
          </div>
        ) : sections.map((section) => (
          section.apps.length ? (
            <section className="sf-section" key={section.id}>
              <div className="sf-section-head">
                <h2>
                  {t(section.title)}
                  <span className={`sf-pill${section.pillClass ? ` ${section.pillClass}` : ""}`}>{t(section.pill)}</span>
                </h2>
                <span className="sf-meta">
                  {coveringPlan && live
                    ? t("subscriptionFlow.allIncluded")
                    : t("appsManagementPage.subscribedCount", {
                      count: section.apps.filter((app) => usageFor(app).manageable).length,
                    })}
                </span>
              </div>
              <div className="sf-tiles">
                {section.apps.map((app) => {
                  const name = (i18n.language === "ar" && app.name_ar)
                    || getLocalizedPlatformName(app.name || app.application || app.appType, t)
                    || app.name;
                  const description = (i18n.language === "ar" && app.description_ar) || app.description;
                  const logo = resolveIntegrationLogo(app) || app.logo;
                  const wideLogo = usesWideIntegrationLogo(app);
                  const disabled = app.status === "Inactive" || app.status === "Coming Soon";
                  const usage = usageFor(app);
                  const date = formatDate(usage.scheduled?.effectiveAt || subscription?.scheduledChange?.effectiveAt || renewsAt, i18n.language);
                  const plan = usage.plan || planForApp(app, plans);
                  const showConnection = !disabled && usage.manageable;
                  const connectionTotal = usage.limit > 0 ? usage.limit : usage.used;
                  let tag = t("appsManagementPage.notSubscribed");
                  let tagClass = "";
                  if (disabled) tag = app.status === "Coming Soon" ? t("appsManagementPage.comingSoonBtn") : t("appsManagementPage.inActiveBtn");
                  else if (coveringPlan && live) {
                    tag = t("appsManagementPage.includedIn", { name: coveringPlan.name });
                    tagClass = "add";
                  } else if (usage.scheduled && Number(usage.scheduled.quantity || 0) < Number(usage.item?.quantity || usage.limit)) {
                    tagClass = "less";
                    tag = Number(usage.scheduled.quantity || 0) === 0
                      ? t("appsManagementPage.endsOn", { date })
                      : t("appsManagementPage.branchesFrom", { count: usage.scheduled.quantity, date });
                  } else if (usage.manageable && usage.limit > 0 && usage.used >= usage.limit) {
                    tag = t("appsManagementPage.activeFull");
                    tagClass = "add";
                  } else if (usage.manageable) {
                    tag = t("appsManagementPage.activeStatus");
                    tagClass = "add";
                  }
                  const freeCount = connectionsForApp(app, connections).filter((row) => row.mode === "free").length;
                  if (!disabled && freeCount > 0) {
                    tag = t("appsManagementPage.freeMode");
                    tagClass = "less";
                  }
                  return (
                    <article key={appId(app)} className="sf-tile is-app">
                      <span className={`sf-tag sf-status${tagClass ? ` ${tagClass}` : ""}`}>{tag}</span>
                      <span className={`sf-logo${wideLogo ? " is-wide" : ""}`}>
                        {logo ? <img src={logo} alt="" onError={(event) => handlePlatformLogoError(event, app)} /> : name.slice(0, 1)}
                      </span>
                      <div className="sf-name">{name}</div>
                      <div className="sf-desc">{description}</div>
                      <div className="sf-tile-foot">
                        <div className="sf-card-line">
                          {showConnection ? (
                            <div className="sf-usage sf-usage-row">
                              <span className="aa-bm-label">
                                {usage.limit > 0
                                  ? t("appsManagementPage.meter", { used: usage.used, total: connectionTotal })
                                  : t("appsManagementPage.connectedOnly", { count: usage.used })}
                              </span>
                              {usage.limit > 0 ? (
                                <span className="sf-meter aa-meter-sm" aria-hidden="true">
                                  <i style={{ width: `${connectionTotal > 0 ? Math.min(100, Math.round((usage.used / connectionTotal) * 100)) : 0}%` }} />
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <div className="sf-price">
                              {plan?.amount ? (
                                <span className="sf-money">
                                  <img src={riyalIcon} alt="" width={12} height={14} />
                                  {formatMinor(plan.amount, i18n.language)}
                                </span>
                              ) : "—"}
                              <small> {t("subscriptionFlow.perBranch")}</small>
                            </div>
                          )}
                        </div>
                        <button type="button" className="sf-btn navy wide" disabled={disabled} onClick={() => openCard(app)}>
                          {disabled
                            ? tag
                            : usage.manageable
                              ? t("appsManagementPage.manage")
                              : t("appsManagementPage.subscribeBtn")}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ) : null
        ))}
      </MDBContainer>
    </div>
  );
}

export default AppManagement;
